import {
  useMemo,
  useState,
  ReactNode,
  useEffect,
  useContext,
  createContext,
  useRef,
} from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { useAdminContent } from "context/admin-content";
import { KioskResults, PatientResults } from "context/admin-content/types";

type Props = {
  children: ReactNode;
  /** Optional pre-loaded data; when provided, skips fetch (used in tests for sync renders) */
  initialData?: RiskProfileContextType;
};

// eslint-disable-next-line react-refresh/only-export-components
export enum RiskKey {
  testing = "testing",
  breastMri = "breastMri",
  lungCt = "lungCt",
  counseling = "counseling",
  average = "average",
}

type RiskProfileContextType = {
  isLoading: boolean;
  patientRiskResult: PatientResults | undefined;
  kioskRiskResult: KioskResults | undefined;
  riskProfile: any;
  isAuth0Session: boolean;
  riskResultKey: RiskKey | undefined;
};

const RiskProfileContext = createContext<RiskProfileContextType | undefined>(
  undefined
);

export function RiskProfileProvider({ children, initialData }: Props) {
  const adminData = useAdminContent();
  const [isLoading, setLoading] = useState(initialData ? false : true);
  const [patientRiskResult, setPatientRiskResult] = useState<
    PatientResults | undefined
  >();
  const [kioskRiskResult, setKioskRiskResult] = useState<
    KioskResults | undefined
  >();
  const [riskProfile, setRiskProfile] = useState<any>(null);
  const [isAuth0Session, setIsAuth0Session] = useState<boolean>(false);
  const hasFetched = useRef(false);
  const [riskResultKey, setRiskResultKey] = useState<RiskKey | undefined>(
    undefined
  );

  useEffect(() => {
    if (initialData) return;
    if (!adminData || adminData?.isLoading || hasFetched.current) {
      return;
    }

    const fetchRiskProfile = async () => {
      // After completing an initial survey, "ciq-questionnaire.sessionId" is set by
      // Questionnaire and this is the patient id.
      // After completing a rescreen, we use "ciq_id", which comes from Auth0 upon login.
      const patientIdFromQuestionnaire = localStorage.getItem(
        "ciq-questionnaire.sessionId"
      );
      const patientIdFromAuth0 = localStorage.getItem("ciq_id");
      const patientId = patientIdFromAuth0 || patientIdFromQuestionnaire;
      setIsAuth0Session(!!patientIdFromAuth0);

      const idToken = localStorage.getItem("id_token");

      try {
        hasFetched.current = true;

        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}/risk_profiles`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          // TODO: Error behaviour? It's a silent call so maybe do not show error to user

          return;
        }

        const riskProfileRes = await response.json();

        setRiskProfile(riskProfileRes.data);

        const guidances = riskProfileRes.data.attributes;

        const {
          testingGuidance,
          mriGuidance,
          lungGuidance,
          counselingGuidance,
        } = guidances;

        if (testingGuidance.meets_criteria) {
          setPatientRiskResult(adminData.patientResults[RiskKey.testing]);
          setKioskRiskResult(adminData.kioskResults[RiskKey.testing]);
          setRiskResultKey(RiskKey.testing);
        } else if (mriGuidance.meets_criteria) {
          setPatientRiskResult(adminData.patientResults[RiskKey.breastMri]);
          setKioskRiskResult(adminData.kioskResults[RiskKey.breastMri]);
          setRiskResultKey(RiskKey.breastMri);
        } else if (lungGuidance.meets_criteria) {
          setPatientRiskResult(adminData.patientResults[RiskKey.lungCt]);
          setKioskRiskResult(adminData.kioskResults[RiskKey.lungCt]);
          setRiskResultKey(RiskKey.lungCt);
        } else if (counselingGuidance.meets_criteria) {
          setPatientRiskResult(adminData.patientResults[RiskKey.counseling]);
          setKioskRiskResult(adminData.kioskResults[RiskKey.counseling]);
          setRiskResultKey(RiskKey.counseling);
        } else {
          setPatientRiskResult(adminData.patientResults[RiskKey.average]);
          setKioskRiskResult(adminData.kioskResults[RiskKey.average]);
          setRiskResultKey(RiskKey.average);
        }
      } catch (error) {
        // What to do for the user here?
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchRiskProfile();
  }, [adminData]);

  const contextProps = useMemo(() => {
    if (initialData) return initialData;
    return {
      patientRiskResult,
      kioskRiskResult,
      riskProfile,
      isLoading,
      isAuth0Session,
      riskResultKey,
    };
  }, [
    initialData,
    isLoading,
    patientRiskResult,
    kioskRiskResult,
    riskProfile,
    isAuth0Session,
    riskResultKey,
  ]);

  return (
    <RiskProfileContext.Provider value={contextProps}>
      {children}
    </RiskProfileContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRiskProfile(): RiskProfileContextType | null {
  const context = useContext(RiskProfileContext);

  if (!context) {
    return null;
  }

  return context;
}
